import { addCalendarDays, type DayKey } from '@cineamo/ui/src/helpers/cinema-time-zone';

import {
    showtimesCalendarDayKey,
    showtimesDayEnd,
    showtimesDayKeyOf,
    todayShowtimesDayKey
} from '../showtimes/showtimesDay';

// Sentinel selectedDate value for the PREVIEWS view ("Vorpremieren"): showings that
// happen strictly before the movie's release date. Release-day showings are regular
// showings and belong to that day's date button, not here.
export const PREVIEWS_SELECTED_DATE = 'previews';

// Value PREVIEWS_SELECTED_DATE had before the PREMIERES → PREVIEWS rename. Still accepted
// in the widget's public query params so embeds on sites we do not control keep working.
export const LEGACY_PREVIEWS_SELECTED_DATE = 'premieres';

export type InitiallySelectedDate = 'all' | 'today' | typeof PREVIEWS_SELECTED_DATE;

/**
 * True while the movie's release day still lies ahead. Only then can previews exist,
 * and only then do the date buttons start at the release date instead of today.
 * A movie releasing today counts as released — there is no "before release date" left.
 *
 * Both days are Berlin days, so this answers the same for every visitor; comparing keys is a
 * string comparison because `YYYY-MM-DD` sorts chronologically.
 */
export const isBeforeReleaseDate = (releaseDate?: string): boolean =>
    !!releaseDate && showtimesCalendarDayKey(releaseDate) > todayShowtimesDayKey();

/**
 * End of the previews window: the last moment of the Cinema Day *before* the release
 * day, so a showing on the release date is never counted as a preview.
 */
export const getPreviewsEndDatetime = (releaseDate: string): Date =>
    showtimesDayEnd(addCalendarDays(showtimesCalendarDayKey(releaseDate), -1));

/**
 * First day the date buttons show.
 *
 * With the PREVIEWS tab live the buttons start at the release date, because everything before it
 * is reachable through that tab. Without it there is nowhere else for earlier showings to live, so
 * starting at the release date hides them: the buttons then start at the movie's first showing
 * instead.
 *
 * Either way the answer is clamped to today — a past release date, a first showing still running
 * this evening, and a start date that is simply not known yet all have to land on today, because
 * a strip that begins in the past lists days nothing can be booked on.
 */
export const getDateButtonsStartDayKey = ({
    releaseDate,
    isPreviewsEnabled,
    earliestShowingStartDatetime
}: {
    releaseDate?: string;
    isPreviewsEnabled?: boolean;
    earliestShowingStartDatetime?: string;
}): DayKey => {
    let startDayKey: DayKey | undefined;
    if (isPreviewsEnabled && releaseDate) {
        startDayKey = showtimesCalendarDayKey(releaseDate);
    } else if (earliestShowingStartDatetime) {
        // The Cinema Day, not the calendar day: a 00:30 screening belongs to the day that began the
        // previous morning, and that is the day its button has to be.
        startDayKey = showtimesDayKeyOf(earliestShowingStartDatetime);
    }

    const today = todayShowtimesDayKey();
    return startDayKey && startDayKey > today ? startDayKey : today;
};

/**
 * Maps the public `initiallySelectedDate` config value to the provider's selectedDate.
 * 'today' resolves to today's date string; 'all' and 'previews' pass through as sentinels.
 * If 'previews' turns out to be unavailable, DateFilter resets the selection to 'all'.
 *
 * Without an explicit value the release date decides: an unreleased movie opens on ALL, a released
 * one on today. ALL is the safe pick because an unreleased movie's date buttons may well start
 * after today — see {@link getDateButtonsStartDayKey}. DateFilter applies the same reset to any
 * selected day the buttons turn out not to cover.
 */
export const resolveInitialSelectedDate = (
    initiallySelectedDate?: InitiallySelectedDate,
    releaseDate?: string
): string => {
    if (initiallySelectedDate === 'all') {
        return 'all';
    }
    if (initiallySelectedDate === PREVIEWS_SELECTED_DATE) {
        return PREVIEWS_SELECTED_DATE;
    }
    if (initiallySelectedDate === undefined && isBeforeReleaseDate(releaseDate)) {
        return 'all';
    }
    return todayShowtimesDayKey();
};

/**
 * Validates the `initiallySelectedDate` query param, returning `undefined` for anything
 * unrecognised so the release-date-based default applies.
 */
export const parseInitiallySelectedDate = (value?: string | null): InitiallySelectedDate | undefined => {
    if (value === 'all' || value === 'today' || value === PREVIEWS_SELECTED_DATE) {
        return value;
    }
    if (value === LEGACY_PREVIEWS_SELECTED_DATE) {
        return PREVIEWS_SELECTED_DATE;
    }
    return undefined;
};
